/**
 * Size a textarea without collapsing a focused page scroller. The optional
 * maxHeight is its existing CSS height limit in pixels; return whether it overflows.
 */
export function resizeAutoGrowTextarea(element: HTMLTextAreaElement, maxHeight?: number): boolean {
  if (typeof CSS !== 'undefined' && CSS.supports?.('field-sizing', 'content')) {
    element.style.setProperty('field-sizing', 'content')
    element.style.removeProperty('height')
    return maxHeight !== undefined && element.scrollHeight > element.clientHeight
  }

  const scrollPositions: Array<[Element, number]> = []
  if (element.scrollTop) scrollPositions.push([element, element.scrollTop])
  for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (ancestor.scrollTop) scrollPositions.push([ancestor, ancestor.scrollTop])
  }
  const rootScroller = document.scrollingElement
  if (rootScroller?.scrollTop && !scrollPositions.some(([node]) => node === rootScroller)) {
    scrollPositions.push([rootScroller, rootScroller.scrollTop])
  }

  // Reading scrollHeight forces layout. Keep the page's old height while the
  // focused textarea temporarily returns to its small, rows-based size.
  const focused = document.activeElement === element
  const previousMargin = element.style.marginBottom
  if (focused) element.style.marginBottom = `${element.offsetHeight}px`

  try {
    element.style.height = 'auto'
    const contentHeight = element.scrollHeight
    element.style.height = `${maxHeight === undefined ? contentHeight : Math.min(contentHeight, maxHeight)}px`
    return maxHeight !== undefined && contentHeight > maxHeight
  } finally {
    if (focused) element.style.marginBottom = previousMargin
    for (const [node, scrollTop] of scrollPositions) node.scrollTop = scrollTop
  }
}
